import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../Components/ui/button";
import { Input } from "../../Components/ui/input";
import {
  DotsHorizontalIcon,
  CaretSortIcon,
  FileIcon,
} from "@radix-ui/react-icons";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/ui/table";

import axios from "axios";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../Components/ui/dropdown-menu";
import DropdownMenuDemo from "../../Components/Filter/AudienceDropdown";
import { useSelector } from "react-redux";
import CreateListDialog from "./CreateListDialog";
import ContactDetailsDialog from "./ContactDetailsDialog";
import { RootState } from "@/src/State/store";
import * as XLSX from "xlsx";
import CircularProgress from "@mui/material/CircularProgress";
import { useToast } from "../../Components/ui/use-toast";
import { Toaster } from "../../Components/ui/toaster";

import { DialogContent } from "../../Components/ui/dialog";
import { Dialog } from "../../Components/ui/dialog";
import { DialogTitle } from "../../Components/ui/dialog";
import { DialogHeader } from "../../Components/ui/dialog";
import { DialogDescription } from "../../Components/ui/dialog";

interface Audience {
  list_id: number;
  listname: string;
  status: string;
  created_date: string;
  total_people: number;
}

interface AudienceFileDetails {
  firstname: string;
  lastname: string;
  phoneno: string;
  location: string;
  filename1: string;
}

interface ApiResponse {
  status: string;
  status_Description: string;
  audienceFileDetails: AudienceFileDetails[];
}

type SortField = 'listname' | 'created_date' | 'total_people';
type SortOrder = 'asc' | 'desc';

interface SortConfig {
  field: SortField | null;
  order: SortOrder;
}

const Audience: React.FC = () => {
  const workspace_id = useSelector(
    (state: RootState) => state.authentication.workspace_id
  );
  const apiUrlAcc = useSelector((state: RootState) => state.authentication.apiURL);

  // UI State
  const [openMenuRowId, setOpenMenuRowId] = useState<number | null>(null);
  const [checkboxSelectedRows, setCheckboxSelectedRows] = useState<number[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  // Data State
  const [audienceList, setAudienceList] = useState<Audience[]>([]);
  const [rawAudienceList, setRawAudienceList] = useState<Audience[]>([]); // Store unsorted data
  const [totalCount, setTotalCount] = useState(0);

  // Pagination & Search State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Sorting State
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: null,
    order: 'asc'
  });

  // Filter State
  const [filterData, setFilterData] = useState({
    filter: "None",
    value: "",
  });
  const [dateList, setDateList] = useState<string[]>([]);

  // Loading States
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);

  // Dialog States
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isInternalDialogOpen, setInternalDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [selectedAudienceId, setSelectedAudienceId] = useState<number>(0);
  const [isApiExecuting, setIsApiExecuting] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [failureCount, setFailureCount] = useState(0);

  // Permissions
  const userPermissions = useSelector(
    (state: RootState) => state.advertiserAccount.permissions
  );

  const toast = useToast();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when search or filter changes (NOT sorting)
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterData]);

  // Fetch data when dependencies change (removed sortConfig from dependency)
  useEffect(() => {
    if (apiUrlAcc) {
      getAudienceList();
    }
  }, [apiUrlAcc, currentPage, rowsPerPage, debouncedSearchTerm, filterData]);

  // Handle dialog changes
  useEffect(() => {
    if (!isDialogOpen && !isDetailsDialogOpen && apiUrlAcc) {
      setSelectedAudienceId(0);
      getAudienceList();
    }
  }, [isDialogOpen, isDetailsDialogOpen]);

  useEffect(() => {
    if (!isInternalDialogOpen) {
      setSelectedAudienceId(0);
    }
  }, [isInternalDialogOpen]);

  // Apply sorting whenever sortConfig or rawAudienceList changes
  useEffect(() => {
    const sortedData = getSortedData(rawAudienceList);
    setAudienceList(sortedData);
  }, [sortConfig, rawAudienceList]);

  // Sorting function
  const handleSort = (field: SortField) => {
    setSortConfig(prevConfig => ({
      field,
      order: prevConfig.field === field && prevConfig.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sorted data
  const getSortedData = (data: Audience[]) => {
    if (!sortConfig.field) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.field!];
      const bValue = b[sortConfig.field!];

      if (sortConfig.field === 'created_date') {
        const dateA = new Date(aValue as string).getTime();
        const dateB = new Date(bValue as string).getTime();
        return sortConfig.order === 'asc' ? dateA - dateB : dateB - dateA;
      }

      if (sortConfig.field === 'total_people') {
        const numA = Number(aValue);
        const numB = Number(bValue);
        return sortConfig.order === 'asc' ? numA - numB : numB - numA;
      }

      // String comparison for listname
      const stringA = String(aValue).toLowerCase();
      const stringB = String(bValue).toLowerCase();

      if (stringA < stringB) return sortConfig.order === 'asc' ? -1 : 1;
      if (stringA > stringB) return sortConfig.order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Render sort icon
  const renderSortIcon = (field: SortField) => {
    return (
      <CaretSortIcon
        className={`w-4 h-4 cursor-pointer ${sortConfig.field === field
            ? sortConfig.order === 'asc'
              ? 'text-gray-700 rotate-180'
              : 'text-gray-700'
            : 'text-gray-400'
          } transition-all duration-200`}
      />
    );
  };

  const getAudienceList = async () => {
    // Show spinner for initial load, table loading for subsequent loads
    if (audienceList.length === 0) {
      setIsInitialLoading(true);
    } else {
      setIsTableLoading(true);
    }

    try {
      const response = await axios.get(`${apiUrlAcc}/GetAudienceListByWorkspaceId`, {
        params: {
          workspace_id: workspace_id,
          pageNumber: currentPage,
          rowsPerPage,
          searchTerm: debouncedSearchTerm,
          criteria: filterData.filter !== "None" ? filterData.filter : null,
          subFilter: filterData.filter !== "None" ? filterData.value : null,
        }
      });

      const result = response.data;
      if (result.status === "Success") {
        const rawData = result.audienceDetails || [];
        setRawAudienceList(rawData); // Store raw data
        setTotalCount(result.totalCount || 0);
        setDateList(result.availableDates || []);
      } else {
        // Handle API failure - clear data and show appropriate message
        setRawAudienceList([]);
        setTotalCount(0);
        setDateList([]);
      }
    } catch (error) {
      console.error("Error fetching audience list:", error);
      setRawAudienceList([]);
      setTotalCount(0);
    } finally {
      setIsInitialLoading(false);
      setIsTableLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const handleCheckboxRowSelect = (id: number) => {
    setCheckboxSelectedRows((prev) => {
      const newSelectedRows = prev.includes(id)
        ? prev.filter((rowId) => rowId !== id)
        : [...prev, id];
      setIsAllSelected(newSelectedRows.length === audienceList.length);
      return newSelectedRows;
    });
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setCheckboxSelectedRows([]);
    } else {
      const allIds = audienceList.map((audience) => audience.list_id);
      setCheckboxSelectedRows(allIds);
    }
    setIsAllSelected(!isAllSelected);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleMenuToggle = (rowId: number) => {
    setOpenMenuRowId(openMenuRowId === rowId ? null : rowId);
  };

  const handleExportButtonClick = () => {
    const filteredData = audienceList.map(
      ({ list_id, listname, created_date, total_people }) => ({
        list_id,
        listname,
        created_date,
        total_people,
      })
    );
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Audience List");
    XLSX.writeFile(workbook, "AudienceList.xlsx");
  };

  const handleEdit = (audienceId: number, listname: string) => {
    setSelectedAudienceId(audienceId);
    setDialogOpen(true);
  };

  const handleDetailsClick = (audienceId: number, total_people: number) => {
    setSelectedAudienceId(audienceId);
    setSuccessCount(total_people);
    setIsDetailsDialogOpen(true);
  };

  const handleInternalOpenDialog = (audienceId: number) => {
    setSelectedAudienceId(audienceId);
    setTimeout(() => {
      setInternalDialogOpen(true);
    }, 0);
  };

  const handleDelete = async (audienceId: number) => {
    setIsApiExecuting(true);
    try {
      const response = await axios.delete(
        `${apiUrlAcc}/Delete_Adv_Audience_ById?Id=${audienceId}`
      );
      if (response.data.status === "Success") {
        toast.toast({
          title: "Success",
          description: "List Deleted Successfully.",
        });
      } else {
        toast.toast({
          title: "Failure",
          description: "List Couldn't Deleted.",
        });
      }
    } catch (error) {
      toast.toast({
        title: "Error",
        description: "Something went wrong, Please try again later.",
      });
    } finally {
      setInternalDialogOpen(false);
      setSelectedAudienceId(0);
      getAudienceList();
      setIsApiExecuting(false);
    }
  };

  const handleDownload = async (audienceId: number, audienceListName: string) => {
    try {
      const response = await axios.get<ApiResponse>(
        `${apiUrlAcc}/Get_Adv_Audience_FileDetails_ById`,
        {
          params: { Id: audienceId },
        }
      );

      if (response.data.status === "Success") {
        const data = response.data.audienceFileDetails;
        const filteredData = data.map(
          ({ firstname, lastname, phoneno, location, filename1 }) => ({
            firstname,
            lastname,
            phoneno,
            location,
            filename1,
          })
        );

        const worksheet = XLSX.utils.json_to_sheet(filteredData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Audience List");
        XLSX.writeFile(workbook, `${audienceListName}_AudienceList.xlsx`);

        toast.toast({
          title: "Success",
          description: "List Exported Successfully",
        });
      } else {
        toast.toast({
          title: "Export Failed",
          description: "Unable to export the list. Please check your internet connection or try again later.",
        });
      }
    } catch (error) {
      toast.toast({
        title: "Error",
        description: "Something went wrong. Please try again later.",
      });
    }
  };

  // Initial loading spinner
  if (isInitialLoading) {
    return (
      <div>
        <Toaster />
        <div className="flex flex-col items-center justify-center h-[500px]">
          <CircularProgress color="primary" />
        </div>
      </div>
    );
  }

  // No data state
  if (!isInitialLoading && totalCount === 0 && !debouncedSearchTerm && filterData.filter === "None") {
    return (
      <div>
        <Toaster />
        <div className="fixed flex justify-end items-end right-0 top-[-15px] z-20 p-4 mr-2">
          {userPermissions.includes("ADV_Audience_Add") && (
            <Button
              onClick={() => setDialogOpen(true)}
              className="w-36 text-sm font-thin h-[35px] mt-[10px]"
            >
              Create list
            </Button>
          )}
          <CreateListDialog
            open={isDialogOpen}
            onOpenChange={setDialogOpen}
            audienceNames={[]}
            audiencefile_id={selectedAudienceId}
          />
        </div>
        <div className="flex flex-col items-center justify-center h-[500px]">
          <h2 className="text-[24px] font-bold mb-1 text-[#000000]">
            Here you will see all your audience lists
          </h2>
          {userPermissions.includes("ADV_Audience_Add") && (
            <div>
              <p className="text-[#64748B] font-normal mb-1 text-[14px]">
                Click the button below to create your first list.
              </p>
              <Button
                onClick={() => setDialogOpen(true)}
                className="inline-flex items-center justify-center text-[14px] font-medium text-[#F8FAFC] px-5 py-5 whitespace-nowrap"
                style={{ width: "auto", minWidth: "unset" }}
              >
                Create list
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Toaster />
      <div className="fixed flex justify-end items-end right-0 top-[-15px] z-20 p-4 mr-2">
        {userPermissions.includes("ADV_Audience_Add") && (
          <Button
            onClick={() => setDialogOpen(true)}
            className="w-36 text-sm font-thin h-[35px] mt-[10px]"
          >
            Create list
          </Button>
        )}
        <CreateListDialog
          open={isDialogOpen}
          onOpenChange={setDialogOpen}
          audienceNames={[]}
          audiencefile_id={selectedAudienceId}
        />
      </div>

      <div className="flex mt-2">
        <div>
          <Input
            placeholder="Search audience by name..."
            className="w-[350px] text-[14px] font-normal text-[#64748B]"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-end ml-auto">
          <DropdownMenuDemo
            setFilterData={setFilterData}
            filterData={filterData}
            dateList={dateList}
          />
          <Button
            variant="outline"
            className="w-full mb-6 ml-4 mt-[-6] text-[#020617] font-medium text-[14px] py-2 px-3"
            onClick={handleExportButtonClick}
          >
            <FileIcon
              style={{ width: "14px", height: "14px" }}
              className="mr-1 text-[#020617]"
            />
            Export
          </Button>
        </div>
      </div>

      <div className="rounded-md border overflow-hidden">
        <div className="max-h-[60vh] overflow-y-auto">
          <Table
            className="rounded-xl whitespace-nowrap border-gray-200"
            style={{ color: "#020202", fontSize: "15px" }}
          >
            <TableHeader className="text-center text-[14px] font-medium">
              <TableRow className="sticky top-0 bg-white z-10 hover:bg-white">
                <TableHead>
                  <div className="flex items-center gap-6 justify-start">
                    <input
                      type="checkbox"
                      className={`text-muted-foreground ${isAllSelected ? "accent-gray-700 bg-grey-700 text-red-500" : ""
                        }`}
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                    />
                    <div
                      className="flex items-center gap-2 cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('listname')}
                    >
                      <span className="font-medium text-[14px] text-[#64748B]">Name</span>
                      {renderSortIcon('listname')}
                    </div>
                  </div>
                </TableHead>
                <TableHead style={{ width: "20%" }}>
                  <div
                    className="flex items-center gap-2 justify-start cursor-pointer hover:text-gray-700"
                    onClick={() => handleSort('created_date')}
                  >
                    <span className="font-medium text-[14px] text-[#64748B]">Updated at</span>
                    {renderSortIcon('created_date')}
                  </div>
                </TableHead>
                <TableHead style={{ width: "10%" }}>
                  <div
                    className="flex items-center gap-2 justify-center cursor-pointer hover:text-gray-700"
                    onClick={() => handleSort('total_people')}
                  >
                    <span className="font-medium text-[14px] text-[#64748B]">Recipients</span>
                    {renderSortIcon('total_people')}
                  </div>
                </TableHead>
                <TableHead className="text-left"></TableHead>
              </TableRow>
            </TableHeader>

            <TableBody className="text-left text-[14px] font-normal text-[#020617]">
              {isTableLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : audienceList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-4 text-[#64748B]">
                    {debouncedSearchTerm || filterData.filter !== "None"
                      ? "No audience list found for the search term"
                      : "No audience lists available"}
                  </TableCell>
                </TableRow>
              ) : (
                audienceList.map((audience) => {
                  const isSelected = checkboxSelectedRows.includes(audience.list_id);
                  return (
                    <TableRow
                      key={audience.list_id}
                      className={`${isSelected ? "bg-gray-200" : ""}`}
                    >
                      <TableCell className="flex justify-start py-4 text-green-900">
                        <div className="flex items-center gap-6">
                          <input
                            type="checkbox"
                            className="accent-gray-700 bg-grey-700 text-red-500"
                            checked={checkboxSelectedRows.includes(audience.list_id)}
                            onChange={() => handleCheckboxRowSelect(audience.list_id)}
                          />
                          <span
                            style={{
                              color: "#020617",
                              fontSize: "14px",
                              fontWeight: "400",
                            }}
                          >
                            {audience.listname}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell style={{ width: "20%" }} className="py-4">
                        <div className="flex items-center gap-2">
                          <span
                            style={{
                              color: "#020617",
                              fontSize: "14px",
                              fontWeight: "400",
                            }}
                          >
                            {new Date(audience.created_date).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })}{" "}
                            ∙{" "}
                            {new Date(audience.created_date).toLocaleTimeString("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell style={{ width: "10%" }} className="py-4 text-right">
                        <span
                          style={{
                            color: "#020617",
                            fontSize: "14px",
                            fontWeight: "400",
                          }}
                        >
                          {audience.total_people}
                        </span>
                      </TableCell>

                      <TableCell className="py-4 pr-4 flex justify-end">
                        {userPermissions.includes("ADV_Audience_Edit") && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <DotsHorizontalIcon
                                style={{ color: "#020617" }}
                                onClick={() => handleMenuToggle(audience.list_id)}
                                className="cursor-pointer w-5 h-5"
                              />
                            </DropdownMenuTrigger>
                            {openMenuRowId === audience.list_id && (
                              <DropdownMenuContent className="w-48 h-auto mr-6">
                                <DropdownMenuItem
                                  className="cursor-pointer"
                                  onClick={() => handleEdit(audience.list_id, audience.listname)}
                                >
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="cursor-pointer"
                                  onClick={() => handleDetailsClick(audience.list_id, audience.total_people)}
                                >
                                  Details
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="cursor-pointer"
                                  onClick={() => handleInternalOpenDialog(audience.list_id)}
                                >
                                  Delete
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="cursor-pointer"
                                  onClick={() => handleDownload(audience.list_id, audience.listname)}
                                >
                                  Download
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            )}
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination controls */}
      <div className="flex justify-between items-center mt-4">
        <div className="flex items-center space-x-2 text-gray-500 text-sm">
          <span>{`${(currentPage - 1) * rowsPerPage + 1}-${Math.min(
            currentPage * rowsPerPage,
            totalCount
          )} of ${totalCount} row(s) selected`}</span>
        </div>

        <div className="flex items-center space-x-4 font-medium text-sm">
          <span className="text-[#020617] font-medium text-[14px]">Rows per page</span>

          <div className="relative inline-block ml-2">
            <select
              className="cursor-pointer border border-gray-300 rounded-md px-2 py-1 appearance-none focus:outline-none focus:ring-1 focus:ring-yellow-400"
              style={{
                width: "60px",
                height: "30px",
                textAlign: "left",
                fontSize: "12px",
                fontWeight: "400",
                borderColor: "#E5E7EB",
                boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.1)",
              }}
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              {[5, 10, 20].map((num) => (
                <option className="text-[12px] font-normal" key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
            <CaretSortIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500 w-4 h-4" />
          </div>

          <div className="ml-4 mr-4">
            <span className="text-[#020617] text-[14px] font-medium">{`Page ${currentPage} of ${totalPages}`}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={currentPage === 1}
              className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === 1 ? "cursor-not-allowed bg-gray-100" : "hover:bg-gray-200"
                }`}
              onClick={() => handlePageChange(1)}
            >
              «
            </button>
            <button
              disabled={currentPage === 1}
              className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === 1 ? "cursor-not-allowed bg-gray-100" : "hover:bg-gray-200"
                }`}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              ‹
            </button>
            <button
              disabled={currentPage === totalPages}
              className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === totalPages ? "cursor-not-allowed bg-gray-100" : "hover:bg-gray-200"
                }`}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              ›
            </button>
            <button
              disabled={currentPage === totalPages}
              className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === totalPages ? "cursor-not-allowed bg-gray-100" : "hover:bg-gray-200"
                }`}
              onClick={() => handlePageChange(totalPages)}
            >
              »
            </button>
          </div>
        </div>
      </div>

      <Dialog open={isInternalDialogOpen} onOpenChange={setInternalDialogOpen}>
        <DialogContent className="p-6 mx-auto">
          <DialogHeader>
            <DialogTitle className="text-18px font-semibold text-[#09090B]">
              Delete List
            </DialogTitle>
            <DialogDescription className="text-14px font-medium text-[#71717A] mt-2">
              Are you sure you want to delete this list? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end gap-4 flex-wrap">
            <Button
              className="px-4 py-2 w-auto"
              variant="outline"
              disabled={isApiExecuting}
              onClick={() => setInternalDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="px-4 py-2 w-auto text-[#FAFAFA] bg-[#3A85F7]"
              disabled={isApiExecuting}
              onClick={() => handleDelete(selectedAudienceId)}
            >
              {isApiExecuting ? "Deleting" : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ContactDetailsDialog
        open={isDetailsDialogOpen}
        onOpenChange={setIsDetailsDialogOpen}
        successCount={successCount}
        failureCount={failureCount}
        apiEndpoint={apiUrlAcc}
        list_id={selectedAudienceId ?? 0}
      />
    </div>
  );
};

export default Audience;